'use client';

import InlineStyle from '@/components/InlineStyle';
import { useEffect, useRef, useState } from 'react';
import { reportFormError } from '@/lib/formMonitor';
import { useDeferredVisible } from '@/lib/useDeferredVisible';
import { HS_BOOK_DEMO_FORM_ID, HS_PORTAL_ID } from '@/lib/hubspotForms';
import { preloadHubSpotForms } from '@/lib/preloadHubSpotForms';
import { useTranslation } from "@/hooks/useTranslation";
const FORM_IDS = {
  'book-a-demo': HS_BOOK_DEMO_FORM_ID,
  'contact': 'a02e1ebe-793e-4c8e-a5bd-831d34464549',
  'partnerships': '7c368dfa-6074-4d6e-8302-07695ce45f90'
};
export default function HubSpotForm({
  formId,
  formName = 'book-a-demo',
  portalId,
  eager = false,
}) {
  const {
    t
  } = useTranslation();
  const activeFormId = formId || FORM_IDS[formName] || FORM_IDS['book-a-demo'];
  const activePortal = portalId || HS_PORTAL_ID;
  const formIdRef = useRef(`hs-${(formId || formName).replace(/[^a-z0-9]/gi, '-')}-form`);
  const divRef = useRef(null);
  const wrapRef = useRef(null);
  const loaded = useRef(false);
  const [status, setStatus] = useState('idle');
  const shouldLoad = useDeferredVisible(wrapRef, {
    eager,
    // Keep HubSpot (~200KB) out of the lab window until the form is near view.
    rootMargin: eager ? '0px' : '200px',
  });

  useEffect(() => {
    if (eager) preloadHubSpotForms();
  }, [eager]);
  useEffect(() => {
    if (!shouldLoad) return;
    if (loaded.current || !activeFormId) return;
    loaded.current = true;
    setStatus('loading');

    // HubSpot postMessage listener — proven approach from WordPress
    function handleHSMessage(event) {
      if (event.data?.type !== 'hsFormCallback') return;
      if (event.data?.eventName !== 'onFormSubmitted') return;
      if (event.data?.id !== activeFormId) return;
      const submission = event.data?.data?.submissionValues || {};
      const email = submission.email || '';
      const phone = submission.phone || submission.phone_number || '';
      const firstName = submission.firstname || submission.first_name || '';
      const lastName = submission.lastname || submission.last_name || '';

      // Store raw values — Google Ads hashes them on their end
      try {
        if (email) sessionStorage.setItem('lb_lead_email', email);
        if (phone) sessionStorage.setItem('lb_lead_phone', phone);
      } catch (e) {}

      // Push to dataLayer for GTM Enhanced Conversions (Google Ads ECL)
      // GA4 event: generate_lead; user_data follows Google's ECL schema
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'generate_lead',
          form_name: formName,
          user_data: {
            email_address: email,
            phone_number: phone,
            ...(firstName && { first_name: firstName }),
            ...(lastName && { last_name: lastName }),
          },
        });
      } catch (e) {}

      // Redirect
      if (activeFormId === FORM_IDS['book-a-demo']) {
        setTimeout(() => {
          window.location.href = '/thank-you/';
        }, 400);
      }
    }
    window.addEventListener('message', handleHSMessage);
    function mount() {
      if (!divRef.current || !window.hbspt) return;
      try {
        window.hbspt.forms.create({
          portalId: activePortal,
          formId: activeFormId,
          target: `#${formIdRef.current}`,
          onFormReady: () => setStatus('ready'),
          onFormSubmitted: () => {}
        });
      } catch (err) {
        setStatus('error');
        reportFormError({
          form: formName,
          error: err.message,
          errorType: 'load_failure'
        });
      }
    }
    // Rewrite HubSpot's verbose free-email error to something concise
    const observer = new MutationObserver(() => {
      if (!divRef.current) return;
      divRef.current.querySelectorAll('.hs-error-msg, .hs-error-msgs li label').forEach(el => {
        if (el.textContent.includes('does not accept addresses from')) {
          el.textContent = 'Work email only';
        }
      });
    });

    if (window.hbspt) {
      mount();
    } else {
      const s = document.createElement('script');
      s.src = 'https://js.hsforms.net/forms/v2.js';
      s.defer = true;
      s.onload = mount;
      s.onerror = () => {
        setStatus('error');
        reportFormError({
          form: formName,
          error: 'HS script blocked',
          errorType: 'script_blocked'
        });
      };
      document.head.appendChild(s);
    }

    if (divRef.current) observer.observe(divRef.current, { childList: true, subtree: true });
    return () => {
      window.removeEventListener('message', handleHSMessage);
      observer.disconnect();
    };
  }, [shouldLoad, activeFormId, activePortal, formName]);
  if (status === 'error') {
    return <div style={{
      background: '#FFF5F5',
      border: '1px solid #FED7D7',
      borderRadius: 12,
      padding: 24,
      textAlign: 'center'
    }}>
        <p style={{
        fontSize: 14,
        fontWeight: 600,
        color: '#C53030',
        fontFamily: "'Inter Tight',sans-serif",
        marginBottom: 6
      }}>{t("hubspot.form_temporarily_unavailable")}</p>
        <p style={{
        fontSize: 13,
        color: '#718096',
        fontFamily: "'Inter Tight',sans-serif",
        marginBottom: 16
      }}>{t("hubspot.please_email_us_directly")}</p>
        <a href="mailto:sales@locobuzz.com" style={{
        display: 'inline-flex',
        padding: '10px 22px',
        borderRadius: 100,
        background: '#4F6EF7',
        color: '#fff',
        fontSize: 13,
        fontWeight: 600,
        textDecoration: 'none',
        fontFamily: "'Inter Tight',sans-serif"
      }}>
          {t("hubspot.email_us")}
        </a>
      </div>;
  }
  return <div ref={wrapRef} style={{
    position: 'relative',
    minHeight: 280
  }}>
      {status === 'loading' && <div style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 12
    }}>
          <div style={{
        width: 32,
        height: 32,
        border: '3px solid #E8ECF4',
        borderTopColor: '#4F6EF7',
        borderRadius: '50%',
        animation: 'hs-spin 0.8s linear infinite'
      }} />
          <p style={{
        fontSize: 13,
        color: '#9CA3AF',
        fontFamily: "'Inter Tight',sans-serif"
      }}>{t("hubspot.loading_form")}</p>
          <InlineStyle css={`@keyframes hs-spin{to{transform:rotate(360deg)}}`} />
        </div>}
      <div id={formIdRef.current} ref={divRef} style={{
      opacity: status === 'loading' ? 0 : 1,
      transition: 'opacity 0.3s',
      width: '100%'
    }} />
    </div>;
}